import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskItem, TaskList } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { MemberRow, taskApi, taskErrorMessage } from '../../utils/taskApi';
import Masonry from '../tasks/Masonry';
import TaskIcon from '../tasks/TaskIcon';
import LoadingSpinner from '../LoadingSpinner';
import ProjectCard from './ProjectCard';
import ProjectDashboard from './dashboard/ProjectDashboard';
import { projectItemUrl } from '../../utils/taskLinks';
import '../../tasks.css';
import '../../projects.css';

interface ArchiveData {
  lists: TaskList[];
  items: TaskItem[];
  members: MemberRow[];
}

/** Every project I own or that's been shared with me. */
const ProjectsPage: React.FC = () => {
  const { ready, error, lists, itemsForList, members, createProject, updateList, lastError, clearError, refresh } = useTasks();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archive, setArchive] = useState<ArchiveData | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [headsUp, setHeadsUpState] = useState(() => {
    try {
      return localStorage.getItem('projects.headsUp') !== '0';
    } catch {
      return true;
    }
  });
  const setHeadsUp = (open: boolean) => {
    setHeadsUpState(open);
    try {
      localStorage.setItem('projects.headsUp', open ? '1' : '0');
    } catch {
      /* per-viewer convenience only */
    }
  };
  // Every project I'm on, whatever the search, for the heads-up.
  const allProjects = useMemo(() => lists.filter((l) => l.kind === 'project'), [lists]);
  const allItems = useMemo(() => allProjects.flatMap((p) => itemsForList(p.id)), [allProjects, itemsForList]);

  const query = search.trim().toLowerCase();
  const projects = useMemo(
    () =>
      lists
        .filter((l) => l.kind === 'project')
        .filter(
          (p) =>
            !query ||
            (p.title || '').toLowerCase().includes(query) ||
            itemsForList(p.id).some((i) => i.text.toLowerCase().includes(query))
        )
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [lists, itemsForList, query]
  );

  const newProject = async () => {
    const project = await createProject({ title: '' });
    if (project) navigate(`/projects/${project.id}?new=1`);
  };

  const loadArchive = async () => {
    setArchiveError(null);
    try {
      const data = await taskApi.archived();
      setArchive({ ...data, lists: data.lists.filter((l) => l.kind === 'project') });
    } catch (e) {
      setArchiveError(taskErrorMessage(e, 'Could not load the archive'));
    }
  };
  const toggleArchive = () => {
    const next = !archiveOpen;
    setArchiveOpen(next);
    if (next) loadArchive();
  };

  if (!ready) {
    if (error) {
      return (
        <div className="tk-page">
          <div className="tk-setup">
            <h2>Projects aren't available yet</h2>
            <p>{error}</p>
            <button type="button" className="tk-btn tk-btn-primary" onClick={refresh}>Try again</button>
          </div>
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  return (
    <div className="tk-page">
      <header className="tk-page-head">
        <div>
          <h1 className="tk-page-title">Projects</h1>
          <p className="tk-page-sub">Long-term plans as mind maps. Show any branch on your Lists, or add a task to this week's list.</p>
        </div>
        <div className="tk-page-actions">
          <button type="button" className="tk-btn tk-btn-primary" onClick={newProject}>
            <TaskIcon name="plus" size={15} /> New project
          </button>
        </div>
      </header>

      <div className="tk-toolbar">
        <label className="tk-search">
          <TaskIcon name="search" size={15} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects" aria-label="Search projects" />
          {search && (
            <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={() => setSearch('')} aria-label="Clear search">
              <TaskIcon name="close" size={13} />
            </button>
          )}
        </label>
      </div>

      {lastError && (
        <div className="tk-error-bar" role="alert">
          <span>{lastError}</span>
          <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={clearError} aria-label="Dismiss"><TaskIcon name="close" size={13} /></button>
        </div>
      )}

      {allItems.length > 0 && (
        <section className="tp-headsup">
          <button type="button" className="tl-section-toggle" onClick={() => setHeadsUp(!headsUp)} aria-expanded={headsUp}>
            <TaskIcon name="chevron-down" size={14} className={headsUp ? '' : 'tl-rot'} />
            <TaskIcon name="bell" size={14} /> Heads up — every project
          </button>
          {headsUp && (
            <ProjectDashboard projects={allProjects} items={allItems} onOpenItem={(item) => navigate(projectItemUrl(item.list_id, item.id))} />
          )}
        </section>
      )}

      {allItems.length > 0 && projects.length > 0 && <h2 className="tk-section-label">Projects</h2>}
      {projects.length ? (
        <Masonry>
          {projects.map((p) => (
            <ProjectCard key={p.id} project={p} items={itemsForList(p.id)} members={members.get(p.id) || []} />
          ))}
        </Masonry>
      ) : (
        <div className="tp-empty">
          <TaskIcon name="project" size={28} />
          <p>{query ? 'No project matches.' : 'No projects yet. Start one for the next build, upgrade or race weekend.'}</p>
          {!query && (
            <button type="button" className="tk-btn tk-btn-primary" onClick={newProject}>
              <TaskIcon name="plus" size={15} /> New project
            </button>
          )}
        </div>
      )}

      <section className="tl-archive">
        <button type="button" className="tl-section-toggle" onClick={toggleArchive} aria-expanded={archiveOpen}>
          <TaskIcon name="chevron-down" size={14} className={archiveOpen ? '' : 'tl-rot'} />
          <TaskIcon name="archive" size={14} /> Archived projects
        </button>
        {archiveOpen && (
          <>
            {archiveError && <p className="tl-error">{archiveError}</p>}
            {!archive && !archiveError && <p className="tk-muted">Loading…</p>}
            {archive && !archive.lists.length && <p className="tk-muted">Nothing archived yet.</p>}
            {archive && archive.lists.length > 0 && (
              <Masonry>
                {archive.lists.map((p) => (
                  <ProjectCard
                    key={p.id}
                    project={p}
                    items={archive.items.filter((i) => i.list_id === p.id)}
                    members={archive.members.filter((m) => m.list_id === p.id).map((m) => m.email)}
                    archived
                    onRestore={async () => {
                      await updateList(p.id, { archived: false });
                      loadArchive();
                    }}
                  />
                ))}
              </Masonry>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default ProjectsPage;
